/**
 * Finds the closest scrollable container in the DOM tree
 * Used to determine whether to scroll a modal or the window
 * Only returns a container if it's actually in a modal context
 */
const getScrollableContainer = (): HTMLElement | null => {
  const form = document.querySelector('form');
  if (!form) return null;

  // Check if we're inside a MUI Modal by looking for the modal backdrop or modal role
  const isInModal =
    !!form.closest('[role="presentation"]') ||
    !!form.closest('[aria-modal="true"]') ||
    !!document.querySelector('.MuiModal-root');

  // If not in a modal, don't look for scrollable containers
  if (!isInModal) return null;

  let parent = form.parentElement;
  while (parent) {
    const overflowY = window.getComputedStyle(parent).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') {
      // Make sure this container is actually scrollable (has overflow content)
      if (parent.scrollHeight > parent.clientHeight) {
        return parent;
      }
    }
    parent = parent.parentElement;
  }
  return null;
};

/**
 * Scrolls to the top of the page or modal container
 * Automatically detects if the form is in a modal and scrolls the appropriate container
 *
 * @param windowScrollPosition - The position to scroll to when scrolling the window (default: 0)
 */
export const scrollToTop = (windowScrollPosition: number = 0): void => {
  const scrollableContainer = getScrollableContainer();

  if (scrollableContainer) {
    // Scroll the modal container to the top
    scrollableContainer.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  } else {
    // Scroll the window (regular page)
    window.scrollTo({
      top: windowScrollPosition,
      behavior: 'smooth',
    });
  }
};
